"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appSettingSchema = exports.iotSupplierSchema = exports.dispatchGroupSchema = exports.jobCodeSchema = exports.vehicleTypeSchema = exports.vendorSchema = exports.customerSchema = exports.customerBaseSchema = exports.depotSchema = exports.locationSchema = exports.serviceLocationSchema = exports.siteSchema = exports.branchSchema = exports.authorizedEmailSchema = exports.vehicleGroupSchema = exports.unitSchema = exports.departmentSchema = exports.companySchema = void 0;
const zod_1 = require("zod");
const common_1 = require("./common");
// ===== Simple single-field entities =====
exports.companySchema = zod_1.z.object({ name: zod_1.z.string().min(1).trim() });
exports.departmentSchema = zod_1.z.object({ name: zod_1.z.string().min(1).trim() });
exports.unitSchema = zod_1.z.object({ name: zod_1.z.string().min(1).trim() });
exports.vehicleGroupSchema = zod_1.z.object({ group: zod_1.z.string().min(1).trim() });
exports.authorizedEmailSchema = zod_1.z.object({ email: zod_1.z.string().email() });
// ===== Branch =====
// Emails are an inline array on Branch; the legacy BranchEmail model has been
// merged in. Empty strings are dropped before persisting.
exports.branchSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    address: common_1.addressSchema,
    phone: zod_1.z.string().nullable().optional(),
    usernames: zod_1.z.array(zod_1.z.string()).optional(),
    emails: zod_1.z.array(zod_1.z.string().trim().email().or(zod_1.z.literal('')))
        .transform((arr) => arr.map((s) => s.trim()).filter((s) => s.length > 0))
        .optional(),
    // CatalogueItem ids the branch stocks. Listed so Zod's .strip() doesn't
    // silently drop the multi-ref picker's selections on save.
    products: zod_1.z.array(common_1.objectIdSchema).optional(),
});
// ===== Site =====
exports.siteSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    company: common_1.objectIdSchema.nullable().optional(),
    // Weightrax credential pair for this site's weighbridge. Set together or
    // not at all (enforced by the route). A blank reg code on update keeps the
    // stored one — it is never sent back to the client.
    weightraxSiteId: zod_1.z.coerce.number().int().positive().nullable().optional(),
    weightraxSiteRegCode: zod_1.z.string().trim().nullable().optional(),
});
// ===== Service Location =====
exports.serviceLocationSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    address: common_1.addressSchema,
    company: common_1.objectIdSchema.nullable().optional(),
});
// ===== Location =====
exports.locationSchema = zod_1.z.object({
    serviceLocation: common_1.objectIdSchema.nullable().optional(),
});
// ===== Depot =====
exports.depotSchema = zod_1.z.object({
    code: zod_1.z.string().min(1).trim(),
    description: zod_1.z.string().nullable().optional(),
    contact: common_1.objectIdSchema.nullable().optional(),
    address: common_1.addressSchema,
});
// ===== Customer =====
// Field shape only — `customerSchema` below adds the Site rule. Exported so
// other schemas can extend it (`.extend` isn't available after a refine).
exports.customerBaseSchema = zod_1.z.object({
    // Required. 50 chars matches the name limit Weightrax/JDE data shows.
    company: zod_1.z.string().trim().min(1).max(50, 'Company must be 50 characters or fewer'),
    // Excluded customers are never pushed to Weightrax and need neither a JDE
    // code nor a Site.
    excludeFromWeightrax: zod_1.z.boolean().optional(),
    // From JDE — entered by hand or by CSV import (AFM never generates it).
    // Required unless excluded; unique across customers.
    jdeCustomerCode: common_1.jdeCodeSchema.nullable().optional(),
    // Sites the customer buys from. Decides which Weightrax site(s) the
    // customer is pushed to; required unless excluded from Weightrax.
    sites: zod_1.z.array(common_1.objectIdSchema).optional(),
    atlasAccountCode: zod_1.z.string().nullable().optional(),
    contactName: common_1.contactNameSchema,
    address: common_1.addressSchema,
    mobile: zod_1.z.string().nullable().optional(),
    email: zod_1.z.string().email().nullable().optional().or(zod_1.z.literal('')),
    role: zod_1.z.string().nullable().optional(),
});
exports.customerSchema = exports.customerBaseSchema.superRefine((v, ctx) => {
    if (v.excludeFromWeightrax)
        return;
    if (!v.jdeCustomerCode) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['jdeCustomerCode'], message: 'Enter the JDE code, or exclude the customer from Weightrax' });
    }
    if (!v.sites?.length) {
        ctx.addIssue({ code: zod_1.z.ZodIssueCode.custom, path: ['sites'], message: 'Pick at least one site, or exclude the customer from Weightrax' });
    }
});
// ===== Vendor =====
exports.vendorSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    status: zod_1.z.enum(['active', 'inactive']), // required: matches the form's required:true
    vendorType: zod_1.z.enum(['Fuel', 'Service']).nullable().optional(),
    contactPerson: common_1.contactNameSchema,
    phone: zod_1.z.string().nullable().optional(),
    email: zod_1.z.string().email().nullable().optional().or(zod_1.z.literal('')),
    website: zod_1.z.string().nullable().optional(),
    address: common_1.addressSchema,
});
// ===== Vehicle Type =====
exports.vehicleTypeSchema = zod_1.z.object({
    type: zod_1.z.string().min(1).trim(),
    meterType: zod_1.z.enum(['Odometer', 'Hubo', 'Engine Hours']).nullable().optional(),
    count: zod_1.z.number().int().min(0).optional(),
    online: zod_1.z.number().int().min(0).optional(),
    offline: zod_1.z.number().int().min(0).optional(),
    notTracked: zod_1.z.number().int().min(0).optional(),
});
// ===== Job Code =====
exports.jobCodeSchema = zod_1.z.object({
    code: zod_1.z.string().min(1).trim(),
    description: zod_1.z.string().nullable().optional(),
});
// ===== Dispatch Group =====
exports.dispatchGroupSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).trim(),
    description: zod_1.z.string().nullable().optional(),
    sortOrder: zod_1.z.number().nullable().optional(),
});
// ===== IoT Supplier =====
const emailTemplateSchema = zod_1.z.object({
    // `from` is an authorized sender email (must be in /authorized-emails).
    // Empty string accepted so the form's "— None —" option round-trips
    // cleanly through Mongoose (which trims & lowercases).
    from: zod_1.z.string().email().nullable().optional().or(zod_1.z.literal('')),
    subject: zod_1.z.string().optional(),
    message: zod_1.z.string().optional(),
    recipients: zod_1.z.string().optional(),
    replyTo: zod_1.z.string().optional(),
    // `enabled` was retired — presence of recipients is the implicit
    // toggle. Schema keeps tolerance for legacy clients sending the field
    // (kept as optional + ignored) so an older bundle doesn't 400 on save.
    enabled: zod_1.z.boolean().optional(),
});
exports.iotSupplierSchema = zod_1.z.object({
    providerName: zod_1.z.string().min(1).trim(),
    phone: zod_1.z.string().nullable().optional(),
    email: zod_1.z.string().email().nullable().optional().or(zod_1.z.literal('')),
    onboardTemplate: emailTemplateSchema.nullable().optional(),
    offboardTemplate: emailTemplateSchema.nullable().optional(),
});
// ===== App Setting =====
exports.appSettingSchema = zod_1.z.object({
    key: zod_1.z.string().min(1).trim(),
    value: zod_1.z.any(),
});
//# sourceMappingURL=master-data.js.map